import useStores from 'shared/hooks/use-stores';
import { useModals, ModalNames } from 'src/components/modals';
import { useCheckout } from 'checkout/context';
import { callNativeMethod } from 'src/mobile-ecomm/hooks/use-mobile-native-bridge';
import { CheckoutVariants } from 'src/constants/checkout-variants';
import { tracker } from 'src/utils/analytics';

type UseInternalLoginReturn = {
  onClickLogin: () => void;
  onClickSignup: () => void;
  onClickLogoutExternalUser: () => void;
  isIsolatedCheckout: boolean;
  isNotLoggedIn: boolean;
  disableGuestCheckout: boolean;
  userHasExternalUser: boolean;
  isMobileApp: boolean;
};

export const useInternalLogin = (): UseInternalLoginReturn => {
  const { User, Cart, UI } = useStores();
  const { variant } = useCheckout();
  const { openModal } = useModals();
  const isIsolatedCheckout = variant === CheckoutVariants.isolatedCheckout;
  const isNotLoggedIn = !User.exists;
  const disableGuestCheckout = Cart.order.dispensary?.storeSettings?.disableGuestCheckout;
  const userHasExternalUser = User.hasExternalUser;
  const isMobileApp = UI.isMobileEcommApp;

  const onClickLogin = (): void => {
    if (UI.isMobileEcommApp) {
      callNativeMethod(`onPromptLogin`);
      return;
    }

    if (UI.isUsingExternalClickHandlerLogin) {
      UI.embeddedEventDispatcher(`embed:clickHandler:login`);
      return;
    }

    void openModal(ModalNames.login);

    tracker.gaGTMClickEvent({
      eventCategory: 'account',
      eventAction: 'signed in',
      eventLabel: `email: {pageType}`,
    });
  };

  const onClickSignup = (): void => {
    void openModal(ModalNames.signUp);

    tracker.gaGTMClickEvent({
      eventCategory: 'account',
      eventAction: 'created account',
      eventLabel: `email: {pageType}`,
    });
  };

  const onClickLogoutExternalUser = (): void => {
    User.removeUserDetailsFromExternalSource();
    Cart.clearMedicalCard();
  };

  return {
    onClickLogin,
    onClickSignup,
    onClickLogoutExternalUser,
    isIsolatedCheckout,
    isNotLoggedIn,
    disableGuestCheckout,
    userHasExternalUser,
    isMobileApp,
  };
};
